import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { AgGridModule } from 'ag-grid-angular';
import {
  FaIconLibrary,
  FontAwesomeModule,
} from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { NgpTabset, NgpTabList, NgpTabButton } from 'ng-primitives/tabs';

import { SuiButtonComponent } from '@sensika/sui/button';
import { SuiButtonGroupComponent } from '@sensika/sui/button-group';
import { SuiComboboxComponent } from '@sensika/sui/combobox';
import {
  SuiMenuComponent,
  SuiMenuHeaderComponent,
  SuiMenuItemComponent,
  SuiMenuSeparatorComponent,
  SuiMenuTriggerDirective,
} from '@sensika/sui/menu';
import { SuiPaginationComponent } from '@sensika/sui/pagination';
import { SuiToolbarComponent } from '@sensika/sui/toolbar';
import { SuiTooltipDirective } from '@sensika/sui/tooltip';
import { SuiBadgeComponent } from '@sensika/sui/badge';
import { SuiCheckboxComponent } from '@sensika/sui/checkbox';
import {
  SuiDateRangePickerComponent,
  SuiDateTimePickerComponent,
} from '@sensika/sui/date-picker';
import {
  SuiFormFieldComponent,
  SuiLabelDirective,
} from '@sensika/sui/form-field';
import { SuiInputComponent } from '@sensika/sui/input';
import {
  SuiSelectComponent,
  SuiSelectOptionComponent,
} from '@sensika/sui/select';
import { SuiSwitchComponent } from '@sensika/sui/switch';
import { AgTextFilterComponent } from './components/ag-text-filter/ag-text-filter.component';
import { AgTextFloatingFilterComponent } from './components/ag-text-filter/ag-text-floating-filter.component';
import { AgNumberFilterComponent } from './components/ag-number-filter/ag-number-filter.component';
import { AgNumberFloatingFilterComponent } from './components/ag-number-filter/ag-number-floating-filter.component';
import { AgSetFilterComponent } from './components/ag-set-filter/ag-set-filter.component';
import { AgDateRangeFilterComponent } from './components/ag-date-range-filter/ag-date-range-filter.component';
import { AgBadgeCellRendererComponent } from './components/cell-renderers/ag-badge-cell-renderer/ag-badge-cell-renderer.component';
import { AgDownloadCellRendererComponent } from './components/cell-renderers/ag-download-cell-renderer/ag-download-cell-renderer.component';
import { AgListCellRendererComponent } from './components/cell-renderers/ag-list-cell-renderer/ag-list-cell-renderer.component';
import { AgMetricCellRendererComponent } from './components/cell-renderers/ag-metric-cell-renderer/ag-metric-cell-renderer.component';
import { AgObjectCellRendererComponent } from './components/cell-renderers/ag-object-cell-renderer/ag-object-cell-renderer.component';
import { AgThumbnailCellRendererComponent } from './components/cell-renderers/ag-thumbnail-cell-renderer/ag-thumbnail-cell-renderer.component';
import { AgTimeCellRendererComponent } from './components/cell-renderers/ag-time-cell-renderer/ag-time-cell-renderer.component';
import { AgToggleCellRendererComponent } from './components/cell-renderers/ag-toggle-cell-renderer/ag-toggle-cell-renderer.component';
import { AgButtonCellRendererComponent } from './components/cell-renderers/button-cell-renderer/button-cell-renderer.component';
import { ObjectTitleRendererComponent } from './components/cell-renderers/object-title-renderer/object-title-renderer.component';
import { AgGridRuleTitleComponent } from './components/cell-renderers/rule-title/rule-title.component';
import { AgGridRuleUsageComponent } from './components/cell-renderers/rule-usage/rule-usage.component';
import { DateRangeSelectionComponent } from './components/date-range-selection/date-range-selection.component';

import { DataGridComponent } from './components/data-grid/data-grid.component';
import { GridToolbarActionDirective } from './components/data-grid/grid-toolbar-action.directive';
import { GridDataViewComponent } from './components/data-grid/grid-data-view.component';
import { GridBulkActionsComponent } from './components/data-grid/grid-bulk-actions.component';
import { GridBulkScopeComponent } from './components/data-grid/grid-bulk-scope.component';
import { GridDetailPaneComponent } from './components/data-grid/grid-detail-pane.component';
import { GridToolbarComponent } from './components/grid-toolbar/grid-toolbar.component';
import { GRID_ICONS } from './grid-icons';

/**
 * The compositional grid stack (app-grid-toolbar + app-data-grid) in a module
 * with NO SharedModule dependency, so SharedModule itself can import and
 * re-export it, so grid views declared in SharedModule (user-editor dialog tabs)
 * compose app-data-grid without a module cycle. SensikaAgGridModule and
 * SharedModule both re-export it; feature modules get it through either.
 */
@NgModule({
  declarations: [
    GridToolbarComponent,
    DataGridComponent,
    GridToolbarActionDirective,
    GridDataViewComponent,
    GridBulkActionsComponent,
    GridBulkScopeComponent,
    GridDetailPaneComponent,
    AgTextFilterComponent,
    AgTextFloatingFilterComponent,
    AgNumberFilterComponent,
    AgNumberFloatingFilterComponent,
    AgSetFilterComponent,
    AgDateRangeFilterComponent,
    AgBadgeCellRendererComponent,
    AgDownloadCellRendererComponent,
    AgListCellRendererComponent,
    AgMetricCellRendererComponent,
    AgObjectCellRendererComponent,
    AgThumbnailCellRendererComponent,
    AgTimeCellRendererComponent,
    AgToggleCellRendererComponent,
    AgButtonCellRendererComponent,
    ObjectTitleRendererComponent,
    AgGridRuleTitleComponent,
    AgGridRuleUsageComponent,
    DateRangeSelectionComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    AgGridModule,
    FontAwesomeModule,
    TranslateModule,
    NgpTabset,
    NgpTabList,
    NgpTabButton,
    SuiButtonComponent,
    SuiButtonGroupComponent,
    SuiComboboxComponent,
    SuiMenuComponent,
    SuiMenuHeaderComponent,
    SuiMenuItemComponent,
    SuiMenuSeparatorComponent,
    SuiMenuTriggerDirective,
    SuiPaginationComponent,
    SuiToolbarComponent,
    SuiTooltipDirective,
    SuiBadgeComponent,
    SuiCheckboxComponent,
    SuiDateRangePickerComponent,
    SuiDateTimePickerComponent,
    SuiFormFieldComponent,
    SuiLabelDirective,
    SuiInputComponent,
    SuiSelectComponent,
    SuiSelectOptionComponent,
    SuiSwitchComponent,
  ],
  exports: [
    GridToolbarComponent,
    DataGridComponent,
    GridToolbarActionDirective,
    GridDataViewComponent,
    GridBulkActionsComponent,
    GridBulkScopeComponent,
    GridDetailPaneComponent,
    AgTextFilterComponent,
    AgTextFloatingFilterComponent,
    AgNumberFilterComponent,
    AgNumberFloatingFilterComponent,
    AgSetFilterComponent,
    AgDateRangeFilterComponent,
    AgBadgeCellRendererComponent,
    AgDownloadCellRendererComponent,
    AgListCellRendererComponent,
    AgMetricCellRendererComponent,
    AgObjectCellRendererComponent,
    AgThumbnailCellRendererComponent,
    AgTimeCellRendererComponent,
    AgToggleCellRendererComponent,
    AgButtonCellRendererComponent,
    ObjectTitleRendererComponent,
    AgGridRuleTitleComponent,
    AgGridRuleUsageComponent,
    DateRangeSelectionComponent,
  ],
})
export class DataGridModule {
  constructor(library: FaIconLibrary) {
    library.addIcons(...GRID_ICONS);
  }
}
